/**
 * Emerald Books — Dimensions: Departments + Projects + Matrix P&L.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  Loader2, Plus, X, Trash2, Pencil, Building2, Briefcase, BarChart3,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { booksClient, errMsg, unwrapList } from './api';

const money = (n) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/* ========================================================================= */
/* Departments                                                                 */
/* ========================================================================= */
export const DepartmentsPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ code: '', name: '', head: '', description: '' });

  const load = async () => {
    setLoading(true);
    try { const r = await booksClient.get('/departments'); setItems(unwrapList(r)); }
    catch (e) { toast.error(errMsg(e)); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm({ code: '', name: '', head: '', description: '' }); setShowForm(true); };
  const openEdit = (d) => { setEditing(d); setForm({ ...d }); setShowForm(true); };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await booksClient.patch(`/departments/${editing.department_id}`, {
          name: form.name, head: form.head, description: form.description,
        });
        toast.success('Updated');
      } else {
        await booksClient.post('/departments', form);
        toast.success('Department created');
      }
      setShowForm(false); load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (id) => {
    if (!window.confirm('Delete department?')) return;
    try { await booksClient.delete(`/departments/${id}`); toast.success('Deleted'); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-[#0b1a2e]">Departments</h1>
          <p className="text-sm text-slate-500">Tag journal & document lines · used in matrix P&L</p>
        </div>
        <Button onClick={openNew} className="bg-[#0f5c3d] hover:bg-[#0d4d33]" data-testid="dept-new-btn">
          <Plus className="w-4 h-4 mr-1" /> New department
        </Button>
      </div>

      {showForm && (
        <form onSubmit={submit} className="bg-white p-4 rounded-lg border grid sm:grid-cols-2 gap-2">
          <Input placeholder="Code *" required value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} disabled={!!editing} />
          <Input placeholder="Name *" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <Input placeholder="Head" value={form.head} onChange={(e) => setForm({ ...form, head: e.target.value })} />
          <Input placeholder="Description" value={form.description || ''} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          <Button type="submit" className="sm:col-span-2 bg-[#0f5c3d] hover:bg-[#0d4d33]" data-testid="dept-submit-btn">
            {editing ? 'Save' : 'Create'}
          </Button>
        </form>
      )}

      {loading ? <Loader2 className="animate-spin mx-auto text-slate-400" /> : (
        <div className="grid gap-2 sm:grid-cols-2" data-testid="dept-list">
          {items.length === 0 && <div className="col-span-full text-center text-slate-400 py-12">No departments yet</div>}
          {items.map((d) => (
            <div key={d.department_id} className="bg-white rounded-lg border p-3 flex items-center gap-3">
              <div className="w-9 h-9 rounded bg-emerald-50 flex items-center justify-center"><Building2 className="w-4 h-4 text-emerald-700" /></div>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-mono text-slate-400">{d.code}</div>
                <div className="font-semibold text-slate-800 truncate">{d.name}</div>
                <div className="text-xs text-slate-500 truncate">{d.head || '—'}{d.description ? ` · ${d.description}` : ''}</div>
              </div>
              <button onClick={() => openEdit(d)} className="p-2 rounded hover:bg-slate-50"><Pencil className="w-4 h-4 text-slate-500" /></button>
              <button onClick={() => del(d.department_id)} className="p-2 rounded hover:bg-rose-50"><Trash2 className="w-4 h-4 text-rose-500" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ========================================================================= */
/* Projects                                                                    */
/* ========================================================================= */
export const ProjectsPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState({
    code: '', name: '', manager: '', budget: 0, currency: 'GBP',
    status: 'active', start_date: '', end_date: '', description: '',
  });

  const load = async () => {
    setLoading(true);
    try { const r = await booksClient.get('/projects'); setItems(unwrapList(r)); }
    catch (e) { toast.error(errMsg(e)); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await booksClient.post('/projects', { ...form, budget: Number(form.budget || 0) });
      toast.success('Project created');
      setShowForm(false); load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (id) => {
    if (!window.confirm('Delete project?')) return;
    try { await booksClient.delete(`/projects/${id}`); toast.success('Deleted'); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const openPL = async (id) => {
    try { const r = await booksClient.get(`/projects/${id}/pl`); setDetail(r.data); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-[#0b1a2e]">Projects</h1>
          <p className="text-sm text-slate-500">Tag lines · project P&L · budget vs actual</p>
        </div>
        <Button onClick={() => setShowForm(true)} className="bg-[#0f5c3d] hover:bg-[#0d4d33]" data-testid="proj-new-btn">
          <Plus className="w-4 h-4 mr-1" /> New project
        </Button>
      </div>

      {showForm && (
        <form onSubmit={submit} className="bg-white p-4 rounded-lg border grid sm:grid-cols-2 gap-2">
          <Input placeholder="Code *" required value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} />
          <Input placeholder="Name *" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <Input placeholder="Manager" value={form.manager} onChange={(e) => setForm({ ...form, manager: e.target.value })} />
          <Input type="number" step="0.01" placeholder="Budget" value={form.budget} onChange={(e) => setForm({ ...form, budget: e.target.value })} />
          <Input placeholder="Currency" value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value.toUpperCase() })} />
          <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="px-3 py-2 border rounded text-sm">
            <option value="active">Active</option>
            <option value="on_hold">On hold</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
          <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
          <Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
          <Input placeholder="Description" className="sm:col-span-2" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          <Button type="submit" className="sm:col-span-2 bg-[#0f5c3d] hover:bg-[#0d4d33]" data-testid="proj-submit-btn">Create</Button>
        </form>
      )}

      {loading ? <Loader2 className="animate-spin mx-auto text-slate-400" /> : (
        <div className="grid gap-2 sm:grid-cols-2" data-testid="proj-list">
          {items.length === 0 && <div className="col-span-full text-center text-slate-400 py-12">No projects yet</div>}
          {items.map((p) => (
            <div key={p.project_id} className="bg-white rounded-lg border p-3 hover:shadow-sm transition">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3 cursor-pointer" onClick={() => openPL(p.project_id)}>
                  <div className="w-9 h-9 rounded bg-indigo-50 flex items-center justify-center"><Briefcase className="w-4 h-4 text-indigo-600" /></div>
                  <div>
                    <div className="text-xs font-mono text-slate-400">{p.code}</div>
                    <div className="font-semibold text-slate-800">{p.name}</div>
                    <div className="text-xs text-slate-500">{p.manager || '—'} · Budget {money(p.budget)} {p.currency} · <span className="uppercase">{p.status}</span></div>
                  </div>
                </div>
                <button onClick={() => del(p.project_id)} className="p-2 rounded hover:bg-rose-50"><Trash2 className="w-4 h-4 text-rose-500" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {detail && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg p-5 w-full max-w-lg">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-xs text-slate-400">{detail.project.code}</div>
                <div className="font-semibold text-slate-800">{detail.project.name} · {detail.project.currency}</div>
              </div>
              <button onClick={() => setDetail(null)}><X className="w-4 h-4" /></button>
            </div>
            <div className="grid grid-cols-3 gap-2 mb-3">
              <div className="p-3 rounded bg-emerald-50 text-center">
                <div className="text-[10px] uppercase tracking-wider text-slate-500">Income</div>
                <div className="font-semibold text-emerald-700">{money(detail.income)}</div>
              </div>
              <div className="p-3 rounded bg-rose-50 text-center">
                <div className="text-[10px] uppercase tracking-wider text-slate-500">Expense</div>
                <div className="font-semibold text-rose-700">{money(detail.expense)}</div>
              </div>
              <div className="p-3 rounded bg-slate-50 text-center">
                <div className="text-[10px] uppercase tracking-wider text-slate-500">Profit</div>
                <div className={`font-semibold ${detail.profit >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>{money(detail.profit)}</div>
              </div>
            </div>
            <div className="mb-3">
              <div className="text-xs text-slate-500 mb-1">Budget {money(detail.budget)} · Utilised {detail.budget_utilised_pct?.toFixed(1)}%</div>
              <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                <div className={`h-full ${detail.budget_utilised_pct > 100 ? 'bg-rose-500' : 'bg-emerald-500'}`}
                  style={{ width: `${Math.min(100, detail.budget_utilised_pct || 0)}%` }} />
              </div>
            </div>
            <div className="max-h-64 overflow-y-auto text-sm">
              {(detail.breakdown || []).map((b) => (
                <div key={b.account_code} className="flex items-center justify-between border-b py-1.5">
                  <div><span className="font-mono text-xs text-slate-400 mr-2">{b.account_code}</span>{b.account_name}</div>
                  <div className={b.type === 'income' ? 'text-emerald-700' : 'text-rose-700'}>{money(b.amount)}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ========================================================================= */
/* Matrix P&L                                                                  */
/* ========================================================================= */
const DIMS = [
  { v: 'department', label: 'Department' },
  { v: 'project', label: 'Project' },
  { v: 'cost_centre', label: 'Cost Centre' },
  { v: 'branch', label: 'Branch' },
];
const METRICS = [
  { v: 'profit', label: 'Profit' },
  { v: 'income', label: 'Income' },
  { v: 'expense', label: 'Expense' },
];
const startOfYear = () => new Date(new Date().getFullYear(), 0, 1).toISOString().slice(0, 10);
const today = () => new Date().toISOString().slice(0, 10);

export const DimensionMatrixPage = () => {
  const [row, setRow] = useState('department');
  const [col, setCol] = useState('project');
  const [metric, setMetric] = useState('profit');
  const [from, setFrom] = useState(startOfYear());
  const [to, setTo] = useState(today());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    if (row === col) { toast.error('Rows and columns must differ'); return; }
    setLoading(true);
    try {
      const r = await booksClient.get('/reports/dimensions/matrix', {
        params: { row_dim: row, col_dim: col, metric, from, to },
      });
      setData(r.data);
    } catch (e) { toast.error(errMsg(e)); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []); // initial

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold text-[#0b1a2e]">Matrix Report</h1>
        <p className="text-sm text-slate-500">Pivot P&L across any two dimensions</p>
      </div>

      <div className="bg-white p-3 rounded-lg border flex flex-wrap gap-2 items-center">
        <label className="text-xs text-slate-500">Rows</label>
        <select value={row} onChange={(e) => setRow(e.target.value)} className="px-3 py-2 border rounded text-sm">
          {DIMS.map((d) => <option key={d.v} value={d.v}>{d.label}</option>)}
        </select>
        <label className="text-xs text-slate-500">Cols</label>
        <select value={col} onChange={(e) => setCol(e.target.value)} className="px-3 py-2 border rounded text-sm">
          {DIMS.map((d) => <option key={d.v} value={d.v}>{d.label}</option>)}
        </select>
        <label className="text-xs text-slate-500">Metric</label>
        <select value={metric} onChange={(e) => setMetric(e.target.value)} className="px-3 py-2 border rounded text-sm">
          {METRICS.map((m) => <option key={m.v} value={m.v}>{m.label}</option>)}
        </select>
        <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-40" />
        <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-40" />
        <Button onClick={load} className="bg-[#0f5c3d] hover:bg-[#0d4d33]" data-testid="matrix-refresh-btn">
          <BarChart3 className="w-4 h-4 mr-1" /> Run
        </Button>
      </div>

      {loading && <Loader2 className="animate-spin mx-auto text-slate-400" />}
      {data && (
        <div className="bg-white rounded-lg border overflow-auto" data-testid="matrix-table">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left px-3 py-2 sticky left-0 bg-slate-50">{DIMS.find((x) => x.v === row).label}</th>
                {data.cols.map((c) => (
                  <th key={c.key} className="text-right px-3 py-2 whitespace-nowrap">{c.label}</th>
                ))}
                <th className="text-right px-3 py-2 bg-emerald-50 text-emerald-800">Total</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => (
                <tr key={r.key} className="border-t hover:bg-slate-50/50">
                  <td className="px-3 py-1.5 sticky left-0 bg-white font-medium">{r.label}</td>
                  {data.cols.map((c) => (
                    <td key={c.key} className="text-right px-3 py-1.5 font-mono text-xs">{money(r.by_col[c.key] || 0)}</td>
                  ))}
                  <td className="text-right px-3 py-1.5 font-mono text-xs bg-emerald-50/40 font-semibold">{money(r.total)}</td>
                </tr>
              ))}
              <tr className="border-t-2 bg-[#0b1a2e] text-white font-semibold">
                <td className="px-3 py-2 sticky left-0 bg-[#0b1a2e]">Total</td>
                {data.cols.map((c) => (
                  <td key={c.key} className="text-right px-3 py-2 font-mono text-xs">{money(c.total)}</td>
                ))}
                <td className="text-right px-3 py-2 font-mono text-emerald-300">{money(data.grand_total)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
